import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react'
import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { PenToolOverlayHandle } from '@/components/app/editor/tools/pen-tool-overlay'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { pathTightBounds } from '@/lib/elements/pen-path-utils'
import { canEnterPenPathNodeEdit } from '@/lib/groups/element-edit-policy'
import { rotatedVisualAabbOfShape } from '@/lib/shapes/shape-coordinates'
import { buildPathFromPoints } from '../helpers/build-path-from-points'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * The pen tool overlay's session: creating a path (or finishing a continued one) when a drawing
 * completes, continuing an open path from node edit, the overlay's reset key and handle, and the
 * shape bounds its smart guides snap to.
 */
export function usePenToolSession({
  internalShapeManagerRef,
  isPenToolActive,
  nodeEditShapeId,
  nodeEditShapeData,
  setNodeEditShapeId,
  setNodeEditShapeData,
  isNodeEditShapeGeometryBlocked,
  onShapeCommitted,
  onNodeEditModeChange,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  isPenToolActive: boolean
  nodeEditShapeId: string | null
  nodeEditShapeData: ShapeData | null
  setNodeEditShapeId: Dispatch<SetStateAction<string | null>>
  setNodeEditShapeData: Dispatch<SetStateAction<ShapeData | null>>
  isNodeEditShapeGeometryBlocked: (shape: ShapeData) => boolean
  onShapeCommitted: CenterCanvasConfig['onShapeCommitted']
  onNodeEditModeChange: CenterCanvasConfig['onNodeEditModeChange']
}) {
  // Ref for tracking which shape is being "continued" via pen-edit sub-mode
  const continuingShapeIdRef = useRef<string | null>(null)
  // Always-current ref so handlePenPathCompleted can read isPenToolActive even
  // when the callback closure was created during an earlier render.
  const isPenToolActiveRef = useRef(isPenToolActive)

  isPenToolActiveRef.current = isPenToolActive
  // Initial points for PenToolOverlay when continuing an existing path
  const [penToolInitialPoints, setPenToolInitialPoints] = useState<PenAnchorPoint[] | undefined>(
    undefined
  )

  // Pen tool: key incremented after each path completion to reset overlay state
  const [penToolOverlayKey, setPenToolOverlayKey] = useState(0)
  const penToolOverlayRef = useRef<PenToolOverlayHandle | null>(null)

  // Rotated VISUAL AABB, matching what smart guides use everywhere else (`getOtherShapeBounds`
  // in shape-manager/hooks/use-smart-guides.ts) and what the shape actually draws — not the raw geometric x/y/width/
  // height, which both ignores rotation and, for an inset shape (triangle, star), stands off the
  // shape's visible edges by its own inset.
  const getPenToolShapeBounds = useCallback(() => {
    return (internalShapeManagerRef.current?.getShapes() ?? [])
      .filter((s) => !s.hidden)
      .map((s) => rotatedVisualAabbOfShape(s))
  }, [internalShapeManagerRef])

  const handlePenPathCompleted = useCallback(
    (points: PenAnchorPoint[], isClosed: boolean) => {
      const mgr = internalShapeManagerRef.current

      if (!mgr || points.length < 2) return

      // Compute tight bounding box including bezier curve extrema so curves that
      // bulge beyond their anchor hull are fully contained in the shape frame.
      const tight = pathTightBounds(points, isClosed)
      const minX = tight?.minX ?? points[0].x
      const minY = tight?.minY ?? points[0].y
      const maxX = tight?.maxX ?? points[0].x
      const maxY = tight?.maxY ?? points[0].y

      const width = Math.max(1, maxX - minX)
      const height = Math.max(1, maxY - minY)
      const { d, pathCommands } = buildPathFromPoints(points, isClosed, minX, minY)

      const continuingId = continuingShapeIdRef.current

      if (continuingId) {
        // Continuation: update the existing shape instead of creating a new one
        continuingShapeIdRef.current = null
        setPenToolInitialPoints(undefined)
        mgr.updateShape(continuingId, { x: minX, y: minY, width, height, path: d, pathCommands })
        const updatedShape = mgr.getShapes().find((s) => s.id === continuingId)

        if (updatedShape) {
          onShapeCommitted?.(updatedShape, 'update')
          const shapesById = new Map(mgr.getShapes().map((shape) => [shape.id, shape]))

          // Only re-enter node-edit if the pen tool is still active. When the user
          // switches to another tool mid-continuation (auto-commit on unmount), the
          // pen tool is already off and node-edit should not reactivate.
          if (isPenToolActiveRef.current && canEnterPenPathNodeEdit(updatedShape, shapesById)) {
            setNodeEditShapeId(continuingId)
            setNodeEditShapeData(updatedShape)
            onNodeEditModeChange?.(true)
          }
        }
      } else {
        const styleOverrides = mgr.getLastUsedStyles()

        mgr.addShape('path', minX, minY, {
          width,
          height,
          anchor: 'top-left',
          path: d,
          pathCommands,
          styleOverrides: {
            fill: styleOverrides.fill ?? '#e5e7eb',
            stroke: styleOverrides.stroke ?? '#000000',
            strokeWidth: styleOverrides.strokeWidth ?? 1,
          },
          selectAfterCreate: false,
        })
      }

      // Reset overlay for next path
      setPenToolOverlayKey((k) => k + 1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onShapeCommitted, onNodeEditModeChange]
  )

  /** Called from NodeEditOverlay when user clicks an open endpoint to continue the path. */
  const handleContinuePath = useCallback(
    (fromEnd: 'first' | 'last', currentPoints: PenAnchorPoint[]) => {
      if (!nodeEditShapeData || isNodeEditShapeGeometryBlocked(nodeEditShapeData)) return
      const ox = nodeEditShapeData.x
      const oy = nodeEditShapeData.y

      // Convert LOCAL coords to canvas coords
      let pts: PenAnchorPoint[] = currentPoints.map((pt) => ({
        ...pt,
        x: pt.x + ox,
        y: pt.y + oy,
        handleIn: pt.handleIn ? { x: pt.handleIn.x + ox, y: pt.handleIn.y + oy } : null,
        handleOut: pt.handleOut ? { x: pt.handleOut.x + ox, y: pt.handleOut.y + oy } : null,
      }))

      if (fromEnd === 'first') {
        // Reverse + swap handles so PenToolOverlay draws forward from the first endpoint
        pts = pts.reverse().map((pt) => ({
          ...pt,
          handleIn: pt.handleOut ? { ...pt.handleOut } : null,
          handleOut: pt.handleIn ? { ...pt.handleIn } : null,
        }))
      }

      continuingShapeIdRef.current = nodeEditShapeId
      setPenToolInitialPoints(pts)
      setNodeEditShapeId(null)
      setNodeEditShapeData(null)
      // Do NOT call onNodeEditModeChange(false) here — the parent would deactivate the pen
      // tool (setSelectedPenTool(null)), preventing PenToolOverlay from mounting for the
      // continuation drawing phase.
      setPenToolOverlayKey((k) => k + 1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isNodeEditShapeGeometryBlocked, nodeEditShapeData, nodeEditShapeId]
  )

  return {
    continuingShapeIdRef,
    penToolInitialPoints,
    penToolOverlayKey,
    setPenToolOverlayKey,
    penToolOverlayRef,
    getPenToolShapeBounds,
    handlePenPathCompleted,
    handleContinuePath,
  }
}
